import { apiClient, handleApiRequest } from './client'
import type {
  DmConversation,
  DmCursor,
  DmDirectoryResponse,
  DmMessage,
  DmMessagePage,
  DmOverviewResponse,
  DmParticipant,
  DmUnreadCountResponse,
} from '@/types/direct-message'

export const directMessageApi = {
  getConversations: (includeArchived = false) =>
    handleApiRequest<DmOverviewResponse>(
      apiClient.get('/api/v1/direct-messages', { params: { include_archived: includeArchived } }),
    ),

  getUnreadCount: () =>
    handleApiRequest<DmUnreadCountResponse>(
      apiClient.get('/api/v1/direct-messages/unread-count'),
    ),

  getDirectory: (q?: string, role?: string, limit = 200) =>
    handleApiRequest<DmDirectoryResponse>(
      apiClient.get('/api/v1/direct-messages/directory', { params: { q, role, limit } }),
    ),

  createConversation: (participantIds: string[], title?: string) =>
    handleApiRequest<DmConversation>(
      apiClient.post('/api/v1/direct-messages', {
        participant_ids: participantIds,
        title: title || undefined,
      }),
    ),

  getConversation: (conversationId: string) =>
    handleApiRequest<DmConversation>(
      apiClient.get(`/api/v1/direct-messages/${conversationId}`),
    ),

  getMessages: (conversationId: string, limit = 50, cursor?: DmCursor) => {
    const params = new URLSearchParams({ limit: String(limit) })
    if (cursor) {
      params.append('before', cursor.before)
      params.append('before_id', cursor.before_id)
    }
    return handleApiRequest<DmMessagePage>(
      apiClient.get(`/api/v1/direct-messages/${conversationId}/messages?${params}`),
    )
  },

  sendMessage: (conversationId: string, body: string) =>
    handleApiRequest<DmMessage>(
      apiClient.post(`/api/v1/direct-messages/${conversationId}/messages`, { body }),
    ),

  deleteMessage: (conversationId: string, messageId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.delete(`/api/v1/direct-messages/${conversationId}/messages/${messageId}`),
    ),

  markAsRead: (conversationId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.post(`/api/v1/direct-messages/${conversationId}/mark-read`),
    ),

  archiveConversation: (conversationId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.post(`/api/v1/direct-messages/${conversationId}/archive`),
    ),

  unarchiveConversation: (conversationId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.post(`/api/v1/direct-messages/${conversationId}/unarchive`),
    ),

  addParticipant: (conversationId: string, userId: string) =>
    handleApiRequest<DmParticipant>(
      apiClient.post(`/api/v1/direct-messages/${conversationId}/participants`, { user_id: userId }),
    ),

  removeParticipant: (conversationId: string, userId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.delete(`/api/v1/direct-messages/${conversationId}/participants/${userId}`),
    ),

  leave: (conversationId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.post(`/api/v1/direct-messages/${conversationId}/leave`),
    ),
}
